// v10/src/components/admin/inventory/modals/EditInventoryItemModal.js
//
// Modal "Editar producto" del Stock de repuestos (A8) — Tarea 2.
// PUT /api/inventory-items/:id
//
// Backend: editables → name, manufacturer, product_code, description,
//   purchase_unit, units_per_purchase, minimum_stock_quantity, notes, is_active.
// Read-only por decisión de schema → category, tracking_mode, stock_unit.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, Save, Edit3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../../services/inventoryService';

const STOCK_UNITS = [
  'unit', 'liter', 'milliliter', 'box', 'pack', 'meter',
  'kg', 'gram', 'gallon', 'quart', 'foot',
];

const EditInventoryItemModal = ({
  isOpen,
  item,
  onClose,
  onSuccess,
  darkMode,
}) => {
  const { t } = useTranslation('admin');

  const isQuantity = item?.tracking_mode === 'quantity';

  const [form, setForm] = useState({
    name: '', manufacturer: '', product_code: '', description: '',
    purchase_unit: '', units_per_purchase: '',
    minimum_stock_quantity: '', notes: '', is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  // Reset al abrir / cuando cambia item
  useEffect(() => {
    if (isOpen && item) {
      setForm({
        name: item.name || '',
        manufacturer: item.manufacturer || '',
        product_code: item.product_code || '',
        description: item.description || '',
        purchase_unit: item.purchase_unit || '',
        units_per_purchase: item.units_per_purchase != null ? String(item.units_per_purchase) : '',
        minimum_stock_quantity: item.minimum_stock_quantity != null ? String(item.minimum_stock_quantity) : '',
        notes: item.notes || '',
        is_active: item.is_active !== false,
      });
      setError('');
    }
  }, [isOpen, item]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validation = useMemo(() => {
    const errors = [];
    if (!form.name.trim()) errors.push(t('inventory_modal_create_validation_name_required'));
    if (form.minimum_stock_quantity !== '') {
      const n = Number(form.minimum_stock_quantity);
      if (!Number.isFinite(n) || n < 0) errors.push(t('inventory_modal_create_validation_minimum_negative'));
    }
    if (isQuantity && form.units_per_purchase !== '') {
      const n = Number(form.units_per_purchase);
      if (!Number.isFinite(n) || n <= 0) errors.push(t('inventory_modal_create_validation_units_per_purchase_positive'));
    }
    return { errors, ok: errors.length === 0 };
  }, [form, isQuantity, t]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validation.ok || submitting || !item?.id) return;

    const payload = {
      name: form.name.trim(),
      manufacturer: form.manufacturer.trim() || null,
      product_code: form.product_code.trim() || null,
      description: form.description.trim() || null,
      minimum_stock_quantity: form.minimum_stock_quantity === '' ? 0 : Number(form.minimum_stock_quantity),
      notes: form.notes.trim() || null,
      is_active: !!form.is_active,
    };
    if (isQuantity) {
      payload.purchase_unit = form.purchase_unit || null;
      payload.units_per_purchase = form.units_per_purchase === '' ? null : Number(form.units_per_purchase);
    }

    setSubmitting(true);
    setError('');
    try {
      const res = await inventoryService.update(item.id, payload);
      if (!isMounted.current) return;
      onSuccess?.(res?.data?.data || null);
      onClose?.();
    } catch (err) {
      if (!isMounted.current) return;
      const msg = err?.response?.data?.message || t('inventory_modal_network_error');
      setError(msg);
    } finally {
      if (isMounted.current) setSubmitting(false);
    }
  };

  if (!isOpen || !item) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const lockedStyle = { ...inputStyle, opacity: 0.55, cursor: 'not-allowed', background: darkMode ? 'rgba(255,255,255,0.02)' : '#f1f5f9' };

  const stockUnitOptions = STOCK_UNITS.map((u) => ({
    key: u,
    label: t(`inventory_purchase_units_${u}`, { defaultValue: u }),
  }));

  const categoryLabel = item.category
    ? t(`inventory_categories_${item.category}`, { defaultValue: item.category })
    : '';
  const trackingLabel = item.tracking_mode
    ? t(`inventory_modal_create_custom_tracking_${item.tracking_mode}`, { defaultValue: item.tracking_mode })
    : '';
  const stockUnitLabel = item.stock_unit
    ? t(`inventory_purchase_units_${item.stock_unit}`, { defaultValue: item.stock_unit })
    : '';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={submitting ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 600, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(79,70,229,0.3)',
            }}>
              <Edit3 style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('inventory_modal_edit_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {t('inventory_modal_edit_subtitle', { name: item.name })}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={submitting}
            aria-label={t('inventory_modal_close')}
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: submitting ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Nombre */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('inventory_modal_create_custom_name_label')}</p>
            <input
              type="text" maxLength={200} style={inputStyle}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </div>

          {/* Categoría / tracking / stock unit (read-only) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 6 }}>
            <div>
              <p style={labelStyle}>{t('inventory_modal_create_custom_category_label')}</p>
              <input type="text" style={lockedStyle} value={categoryLabel} readOnly disabled />
            </div>
            <div>
              <p style={labelStyle}>{t('inventory_modal_create_custom_tracking_label')}</p>
              <input type="text" style={lockedStyle} value={trackingLabel} readOnly disabled />
            </div>
            <div>
              <p style={labelStyle}>{t('inventory_modal_create_custom_stock_unit_label')}</p>
              <input type="text" style={lockedStyle} value={stockUnitLabel} readOnly disabled />
            </div>
          </div>
          <p style={{ margin: '0 0 14px', fontSize: 11, color: fgFaint }}>
            {t('inventory_modal_edit_readonly_hint')}
          </p>

          {/* Marca + código */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <p style={labelStyle}>{t('inventory_modal_create_custom_manufacturer_label')}</p>
              <input
                type="text" maxLength={120} style={inputStyle}
                value={form.manufacturer}
                onChange={(e) => setField('manufacturer', e.target.value)}
                placeholder={t('inventory_modal_create_custom_manufacturer_placeholder')}
              />
            </div>
            <div>
              <p style={labelStyle}>{t('inventory_modal_create_custom_product_code_label')}</p>
              <input
                type="text" maxLength={80} style={inputStyle}
                value={form.product_code}
                onChange={(e) => setField('product_code', e.target.value)}
                placeholder={t('inventory_modal_create_custom_product_code_placeholder')}
              />
            </div>
          </div>

          {/* Descripción */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('inventory_modal_create_custom_description_label')}</p>
            <textarea
              rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder={t('inventory_modal_create_custom_description_placeholder')}
            />
          </div>

          {/* Purchase unit + units per purchase (solo si quantity) */}
          {isQuantity && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <p style={labelStyle}>{t('inventory_modal_create_custom_purchase_unit_label')}</p>
                <select
                  value={form.purchase_unit}
                  onChange={(e) => setField('purchase_unit', e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                >
                  <option value="">{t('inventory_modal_create_custom_purchase_unit_placeholder')}</option>
                  {stockUnitOptions.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <p style={labelStyle}>{t('inventory_modal_create_custom_units_per_purchase_label')}</p>
                <input
                  type="number" min="0" step="0.01" style={inputStyle}
                  value={form.units_per_purchase}
                  onChange={(e) => setField('units_per_purchase', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Stock mínimo */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('inventory_modal_create_minimum_stock_label')}</p>
            <input
              type="number" min="0" step="0.01" style={inputStyle}
              value={form.minimum_stock_quantity}
              onChange={(e) => setField('minimum_stock_quantity', e.target.value)}
            />
            <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
              {t('inventory_modal_create_minimum_stock_hint')}
            </p>
          </div>

          {/* Notas */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('inventory_modal_create_notes_label')}</p>
            <textarea
              rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder={t('inventory_modal_create_notes_placeholder')}
            />
          </div>

          {/* is_active */}
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 12,
              background: form.is_active
                ? (darkMode ? 'rgba(22,163,74,0.10)' : 'rgba(22,163,74,0.06)')
                : (darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9'),
              border: form.is_active
                ? '1px solid rgba(22,163,74,0.30)'
                : (darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0'),
              cursor: 'pointer',
            }}>
              <input
                type="checkbox" checked={form.is_active}
                onChange={(e) => setField('is_active', e.target.checked)}
              />
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: form.is_active ? '#16a34a' : fgPrimary }}>
                  {t('inventory_modal_edit_is_active_label')}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11.5, color: fgMuted }}>
                  {t('inventory_modal_edit_is_active_hint')}
                </p>
              </div>
            </label>
          </div>

          {/* Validation errors */}
          {validation.errors.length > 0 && (
            <div style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}>
              {validation.errors.map((err, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {err}
                </p>
              ))}
            </div>
          )}

          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}>
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <motion.button
              type="submit" disabled={submitting || !validation.ok}
              whileHover={!submitting && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!submitting && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: submitting || !validation.ok ? 'not-allowed' : 'pointer',
                background: submitting || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#4f46e5,#6366f1)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {submitting
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <Save style={{ width: 16, height: 16 }} />
              }
              {submitting
                ? t('inventory_modal_edit_action_loading')
                : t('inventory_modal_edit_action')}
            </motion.button>
            <button
              type="button" onClick={onClose} disabled={submitting}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted, fontFamily: 'inherit',
              }}
            >
              {t('inventory_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default EditInventoryItemModal;
